/**
 * Thin client for the backend `/billing/*` API.
 *
 * Follows the repo convention: axios is configured globally in Routers.jsx
 * (baseURL = REACT_APP_BASE_URL, `Authorization: Token <token>`), so we just
 * call relative paths here. The backend derives the acting organization from
 * the auth token, so we never send organization ids.
 *
 * Billing writes are owner-only on the backend (IsBillingOwner). Non-owners
 * may read; write calls return 403, which callers surface via `message`.
 */
import axios from "axios";

const BASE = "/billing";

// --- catalog (read) ---------------------------------------------------------
export const getProducts = () => axios.get(`${BASE}/products/`, { params: { page_size: 200 } });
export const getPlans = (params = {}) => axios.get(`${BASE}/plans/`, { params: { page_size: 200, ...params } });

// --- plans (management write; manage=1 includes inactive) -------------------
export const getManagedPlans = (params = {}) =>
  axios.get(`${BASE}/plans/`, { params: { page_size: 200, manage: 1, ...params } });
export const createPlan = (payload) => axios.post(`${BASE}/plans/`, payload);
// manage=1 so inactive plans are addressable for edit/delete too.
export const updatePlan = (slug, payload) =>
  axios.patch(`${BASE}/plans/${slug}/`, payload, { params: { manage: 1 } });
export const deletePlan = (slug) =>
  axios.delete(`${BASE}/plans/${slug}/`, { params: { manage: 1 } });

// --- gateway layer (read-only mirror, synced from providers) ----------------
export const getGatewayPlans = (params = {}) =>
  axios.get(`${BASE}/gateway-plans/`, { params: { page_size: 200, ...params } });
export const getGatewaySubscriptions = (params = {}) =>
  axios.get(`${BASE}/gateway-subscriptions/`, { params: { page_size: 200, ...params } });
export const syncGatewayPlans = (payload) =>
  axios.post(`${BASE}/gateway-plans/sync/`, payload);
export const getGatewayOffers = (params = {}) =>
  axios.get(`${BASE}/gateway-offers/`, { params: { page_size: 200, ...params } });
export const syncGatewayOffers = (payload) =>
  axios.post(`${BASE}/gateway-offers/sync/`, payload);
/** Manually register a gateway offer (e.g. a Razorpay offer id from the dashboard). */
export const createGatewayOffer = (payload) =>
  axios.post(`${BASE}/gateway-offers/`, payload);
export const updateGatewayOffer = (id, payload) =>
  axios.patch(`${BASE}/gateway-offers/${id}/`, payload);

// --- dashboard --------------------------------------------------------------
export const getDashboard = () => axios.get(`${BASE}/dashboard/`);

// --- subscriptions ----------------------------------------------------------
export const getSubscriptions = (params = {}) =>
  axios.get(`${BASE}/subscriptions/`, { params: { page_size: 50, ...params } });

export const getSubscription = (id) => axios.get(`${BASE}/subscriptions/${id}/`);

export const subscribe = (payload) => axios.post(`${BASE}/subscriptions/subscribe/`, payload);

export const upgradeSubscription = (id, payload) =>
  axios.post(`${BASE}/subscriptions/${id}/upgrade/`, payload);

export const downgradeSubscription = (id, payload) =>
  axios.post(`${BASE}/subscriptions/${id}/downgrade/`, payload);

/** Choice-1 upgrade: keep the subscription, bill the prorated difference as a debit note. */
export const upgradeInPlace = (id, payload) =>
  axios.post(`${BASE}/subscriptions/${id}/upgrade-in-place/`, payload);

/** Read-only preview of a plan change: amount due (prorated for a mid-cycle upgrade). */
export const quoteSubscriptionChange = (id, payload) =>
  axios.post(`${BASE}/subscriptions/${id}/quote/`, payload);

export const cancelSubscription = (id, payload) =>
  axios.post(`${BASE}/subscriptions/${id}/cancel/`, payload);

export const resumeSubscription = (id) => axios.post(`${BASE}/subscriptions/${id}/resume/`, {});

export const renewSubscription = (id) => axios.post(`${BASE}/subscriptions/${id}/renew/`, {});

/** Operator manual edit of a subscription's status / start / renewal date. */
export const adjustSubscription = (id, payload) =>
  axios.patch(`${BASE}/subscriptions/${id}/adjust/`, payload);

/** Refund a subscription payment (issues a credit note, updates the subscription). */
export const refundSubscription = (id, payload = {}) =>
  axios.post(`${BASE}/subscriptions/${id}/refund/`, payload);

/** Refunds recorded against a subscription's payments, with credit-note numbers. */
export const getSubscriptionRefunds = (id) =>
  axios.get(`${BASE}/subscriptions/${id}/refunds/`);

/** Reconcile an out-of-band gateway payment into the CRM by its payment id. */
export const recordGatewayPayment = (id, payload) =>
  axios.post(`${BASE}/subscriptions/${id}/record-payment/`, payload);

/** All financial documents (invoices, debit/credit notes, refunds) in date order. */
export const getSubscriptionDocuments = (id) =>
  axios.get(`${BASE}/subscriptions/${id}/documents/`);

/** Generate + send a hosted payment link for a debit note. */
export const sendDebitNotePaymentLink = (id, dnId, payload = {}) =>
  axios.post(`${BASE}/subscriptions/${id}/debit-notes/${dnId}/payment-link/`, payload);

// --- attach / detach documents ---------------------------------------------
export const attachCreditNote = (id, creditNoteId) =>
  axios.post(`${BASE}/subscriptions/${id}/attach-credit-note/`, { credit_note_id: creditNoteId });
export const detachCreditNote = (id, creditNoteId) =>
  axios.post(`${BASE}/subscriptions/${id}/detach-credit-note/`, { credit_note_id: creditNoteId });
export const attachDebitNote = (id, debitNoteId) =>
  axios.post(`${BASE}/subscriptions/${id}/attach-debit-note/`, { debit_note_id: debitNoteId });
export const detachDebitNote = (id, debitNoteId) =>
  axios.post(`${BASE}/subscriptions/${id}/detach-debit-note/`, { debit_note_id: debitNoteId });
/** Attach an existing payment to the subscription via one of its invoices. */
export const attachPayment = (id, payload) =>
  axios.post(`${BASE}/subscriptions/${id}/attach-payment/`, payload);
export const detachPayment = (id, paymentId) =>
  axios.post(`${BASE}/subscriptions/${id}/detach-payment/`, { payment_id: paymentId });

// --- document PDF download (endpoints live outside /billing) ----------------
const DOC_PDF_PATH = {
  invoice: (id) => `/invoices/invoices/${id}/pdf/`,
  credit_note: (id) => `/creditnotes/credit-notes/${id}/pdf/`,
  debit_note: (id) => `/creditnotes/debit-notes/${id}/pdf/`,
  payment: (id) => `/invoices/payments/${id}/receiptDownload/`,
};
/** Download a document PDF as a blob (invoice / credit_note / debit_note). */
export const downloadDocumentPdf = (type, id) =>
  axios.post(DOC_PDF_PATH[type](id), {}, { responseType: "blob" });

export const DOCUMENT_TYPES = {
  invoice: { label: "Invoice", color: "blue" },
  debit_note: { label: "Debit note", color: "orange" },
  credit_note: { label: "Credit note", color: "green" },
  refund: { label: "Refund", color: "volcano" },
};

// --- manual reconciliation (operator) --------------------------------------
/** Create a manual/offline subscription (sync-failure recovery). */
export const createManualSubscription = (payload) =>
  axios.post(`${BASE}/subscriptions/manual/`, payload);

/** Confirm an out-of-band payment for a manual subscription. */
export const activateManualSubscription = (id, payload = {}) =>
  axios.post(`${BASE}/subscriptions/${id}/activate-manual/`, payload);

/** Attach an existing TesonCRM invoice to a subscription. */
export const attachInvoice = (id, invoiceId) =>
  axios.post(`${BASE}/subscriptions/${id}/attach-invoice/`, { invoice_id: invoiceId });

/** Detach an invoice from a subscription. */
export const detachInvoice = (id, invoiceId) =>
  axios.post(`${BASE}/subscriptions/${id}/detach-invoice/`, { invoice_id: invoiceId });

/** Set/repair the product's external_reference_id (reconcile a drifted pair). */
export const linkExternal = (id, externalReferenceId) =>
  axios.post(`${BASE}/subscriptions/${id}/link-external/`, {
    external_reference_id: externalReferenceId,
  });

/** Replay state + invoices to the product and re-pull provider status. */
export const resyncSubscription = (id, payload = {}) =>
  axios.post(`${BASE}/subscriptions/${id}/resync/`, payload);

/** List all invoices attached to a subscription (one-to-many). */
export const getSubscriptionInvoices = (id) =>
  axios.get(`${BASE}/subscriptions/${id}/invoices/`);

/** List payments recorded against a subscription's invoices. */
export const getSubscriptionPayments = (id) =>
  axios.get(`${BASE}/subscriptions/${id}/payments/`);

/** Change history (who/when/what) for a subscription, newest first. */
export const getSubscriptionHistory = (id) =>
  axios.get(`${BASE}/subscriptions/${id}/history/`);

// --- manual gateway-subscription management ---------------------------------
/** Cancel one gateway subscription under a Teson subscription. */
export const cancelGatewaySubscription = (id, payload) =>
  axios.post(`${BASE}/subscriptions/${id}/gateway-subscriptions/cancel/`, payload);

/** Create a new (current) gateway subscription on a chosen provider. */
export const createGatewaySubscription = (id, payload) =>
  axios.post(`${BASE}/subscriptions/${id}/gateway-subscriptions/create/`, payload);

/** Attach an existing provider subscription id as a gateway subscription. */
export const attachGatewaySubscription = (id, payload) =>
  axios.post(`${BASE}/subscriptions/${id}/gateway-subscriptions/attach/`, payload);

// --- offers -----------------------------------------------------------------
export const applyOffer = (payload) => axios.post(`${BASE}/offers/apply/`, payload);
/** Active, in-window offers (with codes) for the subscribe offer dropdown. */
export const getAvailableOffers = (params = {}) =>
  axios.get(`${BASE}/offers/available/`, { params });

// --- history ----------------------------------------------------------------
export const getPayments = () => axios.get(`${BASE}/payments/`);
export const getInvoices = () => axios.get(`${BASE}/invoices/`);

/** Pull a human-readable message out of a DRF/billing error response. */
export const errorMessage = (err, fallback = "Something went wrong.") => {
  const data = err?.response?.data;
  if (!data) return err?.message || fallback;
  if (typeof data === "string") return data;
  if (data.error) return data.error;
  if (data.detail) return data.detail;
  // DRF field errors: {field: ["msg"]}
  const first = Object.values(data)[0];
  if (Array.isArray(first)) return first[0];
  return fallback;
};

export const SUBSCRIPTION_STATUS_COLORS = {
  trial: "blue",
  pending: "orange",
  active: "green",
  paused: "purple",
  grace: "volcano",
  cancelled: "default",
  expired: "default",
  failed: "red",
  incomplete: "default",
};
